import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";
import ImportExportBar from "../../finance/shared/ImportExportBar";
import { TableToolbar } from "../../finance/shared/TableToolbar";
import RelacionesAlert from "../../../shared/components/RelacionesAlert";
import { friendlyError } from "../../../shared/utils/apiErrors";
import styles from "../../finance/invoices/styles/invoice.module.css";

function showToast(msg, ok = true) {
  const existing = document.getElementById("__condo_toast__");
  if (existing) existing.remove();
  const el = document.createElement("div");
  el.id = "__condo_toast__";
  el.style.cssText = [
    "position:fixed","bottom:24px","right:24px","z-index:9999",
    "padding:12px 20px","border-radius:10px","font-size:0.88rem",
    "font-weight:600","max-width:400px","word-break:break-word",
    "box-shadow:0 8px 32px rgba(0,0,0,0.45)",
    ok
      ? "background:rgba(34,197,94,0.15);border:1px solid rgba(34,197,94,0.4);color:#86efac"
      : "background:rgba(239,68,68,0.15);border:1px solid rgba(239,68,68,0.4);color:#fca5a5",
  ].join(";");
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4000);
}

export default function ListProveedoresPage() {
  const { t } = useTranslation();
  const TIPO_LABEL = {
    FISICA: t("proveedores.tipoFisica"), JURIDICA: t("proveedores.tipoJuridica"), DIMEX: "DIMEX",
    NITE: "NITE", PASAPORTE: t("proveedores.tipoPasaporte"),
    EXTRANJERO: t("proveedores.tipoExtranjero"), OTRO: t("proveedores.tipoOtro"),
  };
  const navigate = useNavigate();
  const [proveedores,   setProveedores]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [page,          setPage]          = useState(1);
  const [count,         setCount]         = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteRel,     setDeleteRel]     = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [importResult,  setImportResult]  = useState(null);

  const PAGE_SIZE  = 8;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  async function openDelete(p) {
    setDeleteConfirm(p);
    setDeleteRel(null);
    try {
      const r = await apiFetch(`/api/proveedores/proveedor/${p.id}/relaciones/`);
      if (r.ok) setDeleteRel(await r.json());
    } catch { /* el modal funciona igual sin el detalle */ }
  }

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page });
      if (search.trim()) qs.set("search", search.trim());
      const r = await apiFetch(`/api/proveedores/proveedor/?${qs}`);
      if (!r.ok) { showToast(t("proveedores.errorCargar"), false); return; }
      const data = await r.json();
      setProveedores(data.results ?? (Array.isArray(data) ? data : []));
      setCount(data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch { showToast(t("proveedores.errorConexion"), false); }
    finally { setLoading(false); }
  }, [page, search, t]);

  // Debounce: al escribir en la búsqueda no dispara fetch por cada tecla.
  useEffect(() => {
    const timer = setTimeout(() => cargar(), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [cargar]);

  async function handleDelete(id) {
    setDeleting(true);
    try {
      const r = await apiFetch(`/api/proveedores/proveedor/${id}/`, { method: "DELETE" });
      if (!r.ok) {
        const msg = await friendlyError(r, t("proveedores.errorEliminar"));
        showToast(msg, false);
        return;
      }
      showToast(t("proveedores.eliminado"));
      setDeleteConfirm(null);
      setDeleteRel(null);
      await cargar();
    } catch { showToast(t("proveedores.errorConexion"), false); }
    finally { setDeleting(false); }
  }


  return (
    <div>
      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/"><span className={styles.inactive}>
          {t("common.inicio", "Inicio")} /
        </span></Link>
        <span className={styles.active}> {t("proveedores.titulo")}</span>
      </span>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start",
        marginTop:12, marginBottom:12, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ margin:0, fontFamily:"'Manrope',sans-serif" }}>
            {t("proveedores.titulo")}
          </h2>
          <p style={{ margin:"4px 0 0", color:"var(--text-secondary)", fontSize:"0.82rem" }}>
            {t("proveedores.subtitulo")}
          </p>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <ImportExportBar
            plantillaUrl="/api/proveedores/proveedor/plantilla-excel/"
            importarUrl="/api/proveedores/proveedor/importar/"
            exportarUrl="/api/proveedores/proveedor/exportar/"
            plantillaFilename="plantilla_proveedores.xlsx"
            exportarFilename="proveedores.xlsx"
            onResult={(r) => { setImportResult(r); if (r?.ok) cargar(); }}
          />
          <button
            onClick={() => navigate("/proveedores/agregar")}
            className={styles["btn-add-factura"]}
            style={{ borderRadius:8, padding:"8px 16px", fontSize:"0.85rem",
              fontWeight:700, display:"flex", alignItems:"center", gap:6 }}
          >
            <i className="fa-solid fa-plus" />{t("proveedores.nuevoProveedor")}
          </button>
        </div>
      </div>

      {/* Resultado importación */}
      {importResult && (
        <div style={{
          padding:"10px 14px", borderRadius:8, marginBottom:12, fontSize:"0.82rem",
          background: importResult.ok ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
          border:`1px solid ${importResult.ok ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
          color: importResult.ok ? "#4ade80" : "#f87171",
          display:"flex", justifyContent:"space-between", alignItems:"center",
        }}>
          <span>{importResult.msg}</span>
          <button onClick={() => setImportResult(null)}
            style={{ background:"none", border:"none", color:"inherit", cursor:"pointer", fontSize:"1.1rem" }}>×
          </button>
        </div>
      )}

      <RelacionesAlert campos={["rel_cuenta_proveedores"]} />

      {/* Tabla contenedora — mismo estilo que facturas */}
      <div className={styles["table-container"]}>
        {/* Buscador */}
        <TableToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder={t("proveedores.buscarPlaceholder")}
          loading={loading}
        />

        {/* Tabla */}
        <div className={`${styles["table-body"]} ${styles["table-responsive"]}`}>
          {loading ? (
            <p style={{ padding:24, color:"var(--text-secondary)", textAlign:"center", fontSize:"0.84rem" }}>
              <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight:8 }} />
              {t("common.cargando")}
            </p>
          ) : proveedores.length === 0 ? (
            <p style={{ padding:40, color:"var(--text-secondary)", textAlign:"center", fontSize:"0.84rem" }}>
              {search
                ? t("proveedores.sinResultadosBusqueda")
                : t("proveedores.sinProveedoresList")}
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t("proveedores.colNombre")}</th>
                  <th>{t("proveedores.colCedula")}</th>
                  <th>{t("proveedores.colEmail")}</th>
                  <th>{t("proveedores.colTelefonos")}</th>
                  <th>{t("proveedores.colCuenta")}</th>
                  <th>{t("proveedores.colEstado")}</th>
                  <th style={{ textAlign:"center" }}>{t("common.acciones")}</th>
                </tr>
              </thead>
              <tbody>
                {proveedores.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span style={{ fontWeight:600, color:"var(--text-primary)" }}>{p.nombre}</span>
                      <br />
                      <span style={{ fontSize:"0.73rem", color:"var(--text-muted)" }}>
                        {TIPO_LABEL[p.tipo_cedula] || p.tipo_cedula}
                      </span>
                    </td>
                    <td>{p.cedula || <span style={{ color:"var(--text-muted)" }}>—</span>}</td>
                    <td>{p.email || <span style={{ color:"var(--text-muted)" }}>—</span>}</td>
                    <td>
                      {p.telefonos?.length > 0
                        ? p.telefonos.map((tel) => (
                            <div key={tel.id} style={{ fontSize:"0.78rem" }}>
                              {tel.codigo_pais} {tel.numero}
                              <span style={{ marginLeft:4, fontSize:"0.7rem",
                                background:"var(--hover-bg)", borderRadius:4, padding:"1px 5px" }}>
                                {tel.tipo}
                              </span>
                            </div>
                          ))
                        : <span style={{ color:"var(--text-muted)" }}>—</span>}
                    </td>
                    <td>
                      {p.cuenta_codigo
                        ? <span style={{ fontSize:"0.78rem", color:"#60a5fa" }}>{p.cuenta_codigo}</span>
                        : <span style={{ fontSize:"0.75rem", color:"#fbbf24" }}>
                            {t("proveedores.sinCuenta")}
                          </span>}
                    </td>
                    <td>
                      <span style={{
                        fontSize:"0.75rem", fontWeight:700, padding:"3px 9px", borderRadius:5,
                        background: p.activo ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.1)",
                        color: p.activo ? "#4ade80" : "#f87171",
                      }}>
                        {p.activo ? t("proveedores.activo") : t("proveedores.inactivo")}
                      </span>
                    </td>
                    <td style={{ textAlign:"center" }}>
                      <div style={{ display:"inline-flex", gap:6, justifyContent:"center" }}>
                        <button
                          type="button"
                          onClick={() => navigate(`/proveedores/editar/${p.id}`)}
                          title={t("common.editar")}
                          style={{
                            display:"inline-flex", alignItems:"center", justifyContent:"center",
                            width:30, height:30, padding:0, borderRadius:6, cursor:"pointer", fontSize:"0.85rem",
                            background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.3)", color:"#f97316",
                            transition:"background 0.15s, border-color 0.15s",
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.background="rgba(249,115,22,0.2)"; e.currentTarget.style.borderColor="#f97316"; }}
                          onMouseOut={(e)  => { e.currentTarget.style.background="rgba(249,115,22,0.1)"; e.currentTarget.style.borderColor="rgba(249,115,22,0.3)"; }}
                        >
                          <i className="fa-solid fa-pen" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openDelete(p)}
                          title={t("common.eliminar")}
                          style={{
                            display:"inline-flex", alignItems:"center", justifyContent:"center",
                            width:30, height:30, padding:0, borderRadius:6, cursor:"pointer", fontSize:"0.85rem",
                            background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.25)", color:"#f87171",
                            transition:"background 0.15s, border-color 0.15s",
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.background="rgba(239,68,68,0.2)"; e.currentTarget.style.borderColor="#f87171"; }}
                          onMouseOut={(e)  => { e.currentTarget.style.background="rgba(239,68,68,0.1)"; e.currentTarget.style.borderColor="rgba(239,68,68,0.25)"; }}
                        >
                          <i className="fa-solid fa-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer con paginación */}
        <div className={styles["table-footer"]}>
          <p style={{ color:"var(--text-secondary)", fontSize:"0.83rem", margin:0 }}>
            {loading
              ? <><i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight:5 }} />{t("common.cargando")}</>
              : <><span>{count}</span> {t("common.resultados")} · {t("common.pagina")} <span>{page}</span> {t("common.de")} <span>{totalPages}</span></>}
          </p>
          <div className={`${styles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              className={`btn btn-sm ${styles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              className={`btn btn-sm ${styles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {/* Modal eliminar */}
      {deleteConfirm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
          backdropFilter:"blur(3px)", zIndex:9999,
          display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => !deleting && (setDeleteConfirm(null), setDeleteRel(null))}>
          <div style={{ background:"var(--bg-card)", border:"1px solid var(--border-card)",
            borderRadius:12, padding:24, maxWidth:460, width:"92%",
            boxShadow:"0 24px 64px rgba(0,0,0,0.55)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign:"center", marginBottom:16 }}>
              <div style={{ fontSize:"2.5rem", marginBottom:8 }}>🗑️</div>
              <h3 style={{ color:"#f87171", margin:0 }}>{t("proveedores.eliminarTitulo")}</h3>
              <p style={{ color:"var(--text-secondary)", marginTop:6, fontSize:"0.88rem" }}>
                {t("proveedores.eliminarConfirm", { nombre: deleteConfirm.nombre })}
              </p>
            </div>

            {/* Cuenta del catálogo */}
            {(deleteRel?.cuenta_codigo ?? deleteConfirm.cuenta_codigo) && (
              <div style={{ background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.2)",
                borderRadius:8, padding:"8px 12px", marginBottom:10,
                fontSize:"0.78rem", color:"#fbbf24" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight:6 }} />
                {t("proveedores.eliminarCuentaCatalogo")}{" "}
                <strong>{deleteRel?.cuenta_codigo ?? deleteConfirm.cuenta_codigo}</strong>
              </div>
            )}

            {/* Documentos relacionados que se borran en cascada */}
            {deleteRel?.tiene_relacionados && (
              <div style={{ background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.3)",
                borderLeft:"3px solid #f87171", borderRadius:8, padding:"10px 12px", marginBottom:14,
                fontSize:"0.8rem", color:"#fca5a5" }}>
                <div style={{ marginBottom:6, fontWeight:600 }}>
                  <i className="fa-solid fa-circle-exclamation" style={{ marginRight:6 }} />
                  {t("proveedores.eliminarRelacionados")}
                </div>
                <ul style={{ margin:"0 0 0 18px", padding:0 }}>
                  {deleteRel.facturas_cxp > 0 && <li>{t("proveedores.relFacturasCxp", { count: deleteRel.facturas_cxp })}</li>}
                  {deleteRel.recibos_cxp > 0 && <li>{t("proveedores.relRecibosCxp", { count: deleteRel.recibos_cxp })}</li>}
                  {deleteRel.asientos > 0 && <li>{t("proveedores.relAsientos", { count: deleteRel.asientos })}</li>}
                </ul>
                <div style={{ marginTop:6, fontSize:"0.74rem" }}>
                  {t("proveedores.eliminarSaldos")} <strong>{t("proveedores.accionIrreversible")}</strong>
                </div>
              </div>
            )}

            <div style={{ display:"flex", gap:10, justifyContent:"center", marginTop:8 }}>
              <button onClick={() => { setDeleteConfirm(null); setDeleteRel(null); }} disabled={deleting}
                className="btn btn-outline-secondary btn-sm">
                {t("common.cancelar")}
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)} disabled={deleting}
                className="btn btn-sm"
                style={{ background:"rgba(239,68,68,0.15)", border:"1px solid rgba(239,68,68,0.3)",
                  color:"#f87171", fontWeight:700 }}>
                {deleting
                  ? <><i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight:6 }} />{t("common.eliminando")}</>
                  : <>{t("common.si")}, {t("common.eliminar")}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
